import { Injectable, NgZone, inject } from '@angular/core';
import { ActivationStart, Router } from '@angular/router';

import { PerformanceProfilingConfig } from '@frontend/sports/common/client-config-data-access';
import { SportsProductUtils } from '@frontend/sports/host-app/sports-product/feature/utils';
import { OnAppInit } from '@frontend/vanilla/core';
import { filter } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class PerformanceProfilingService implements OnAppInit {
    private router = inject(Router);
    private performanceConfig = inject(PerformanceProfilingConfig);
    private productUtils = inject(SportsProductUtils);

    private readonly markNameTTI = 'vn_tti';

    private lastTrackedUrl = '';
    private zone = inject(NgZone);

    onAppInit() {
        this.zone.runOutsideAngular(() =>
            setTimeout(() => {
                this.register();
            }),
        );
    }

    register(): void {
        try {
            const profilingEnabled = this.performanceConfig.isPerformanceProfilingEnabled;
            const browserSupport = performance && performance.measure;

            if (profilingEnabled && !!browserSupport) {
                this.router.events
                    .pipe(
                        filter((event) => event instanceof ActivationStart),
                        this.productUtils.filterSports(),
                    )
                    .subscribe(() => this.navigationStartCallback());
            }
            // eslint-disable-next-line no-empty
        } catch {}
    }

    markTTI(): void {
        if (!this.performanceConfig.isPerformanceProfilingEnabled) {
            return;
        }

        this.markEnd(this.markNameTTI);
    }

    private markEnd(markName: string): void {
        try {
            // markNameTTI is tracked from the grid. if there are more grids on the page we want to only track once'
            if (markName === this.markNameTTI && this.lastTrackedUrl !== this.router.url) {
                performance.mark(`${markName}-end`);
                const navigationStartMark = performance.getEntriesByName(`${markName}-start`)[0];
                if (navigationStartMark) {
                    performance.measure(markName, `${markName}-start`, `${markName}-end`);
                }
                if (markName === this.markNameTTI) {
                    this.lastTrackedUrl = this.router.url;
                }
            }
            // eslint-disable-next-line no-empty
        } catch {}
    }

    private navigationStartCallback = () => {
        this.markStart();
    };

    private markStart(): void {
        try {
            performance.clearMarks(`${this.markNameTTI}-start`);
            performance.clearMarks(`${this.markNameTTI}-end`);
            performance.clearMeasures(this.markNameTTI);

            performance.mark(`${this.markNameTTI}-start`);
            // eslint-disable-next-line no-empty
        } catch {}
    }
}
